import { injectable } from 'inversify';

import { FormatService } from '@vk-hr-tek/core/format';
import { TypographyColor } from '@vk-hr-tek/ui/Typography';
import { ChipColorUnion } from '@vk-hr-tek/ui/ColoredChip';

import {
  UserGroup,
  UserResponse,
  ManagerSubstitutesEventType,
  UkepCertificate,
  Attorney,
  ManageableCompanyItem,
  ManagerSubstitute,
} from '@app/gen/users';
import {
  ManagerSubstituteAbsenceOption,
  ProfileItem,
  DateRange,
} from '@app/gen/settings';
import {
  AttorneyType,
  AttorneyStatus,
  AttorneyReadableStatus,
} from '@app/types';
import { ATTORNEYS_POWERS_DEFAULT } from '@app/constants';

import { CompanyPositionSubstitutes } from '../slice/user.state';
import { SubstituteType } from '../types';

@injectable()
export class UserMapper {
  constructor(private format: FormatService) {}

  process(
    user: Omit<UserResponse, 'certificate'> & {
      certificate: Omit<UserResponse['certificate'], 'status'> & {
        status:
          | 'waiting_to_create'
          | 'need_verify'
          | 'waiting_confirmation'
          | 'verifying'
          | 'released'
          | 'expired'
          | 'error'
          | 'need_verify_code'
          | 'release_paused';
      };
    },
  ) {
    return {
      id: user.id,
      name: `${user.last_name} ${user.first_name} ${user.middle_name}`,
      birthday: user.birth_date,
      avatar: `${user.last_name[0]}${user.first_name[0]}`.toUpperCase(),
      snils: this.format.toSnils(user.snils),
      phone: this.format.toPhone(user.phone),
      certificate: {
        exists: user.certificate.exists,
        type: user.certificate.sign_type === 'ukep' ? 'УКЭП' : 'УНЭП',
        description:
          user.certificate.sign_type === 'ukep'
            ? 'УКЭП (Усиленная квалифицированная электронная подпись)'
            : 'УНЭП (Усиленная неквалифицированная электронная подпись)',
        activity:
          user.certificate.valid_from && user.certificate.valid_to
            ? `Действителен: с ${this.format.toDate(
                user.certificate.valid_from,
              )} по ${this.format.toDate(user.certificate.valid_to)}`
            : 'У вас нет действующей электронной подписи этого типа',
      },
      notifications: {
        telegram: user.telegram_enabled,
      },
      groups: this.processGroups(user.groups, user.attorneys),
      employees: user.employees.map((employee) => ({
        ...employee,
        dismissed_at: employee.dismissed_at
          ? this.format.toDate(employee.dismissed_at)
          : '',
        isDismissed:
          !!employee.dismissed_at &&
          !Number.isNaN(Date.parse(employee.dismissed_at)) &&
          new Date(employee.dismissed_at) <= new Date(),
        editSubstitutes: employee.permissions.edit_substitutes,
        vacation_days: employee.vacation_days,
        groups: this.processGroups(user.groups, user.attorneys).reduce(
          (
            acc: {
              id: string;
              name: string;
              roles: {
                id: string;
                name: string;
                profiles?: ProfileItem[];
              }[];
              attorneys: AttorneyType[];
            },
            group,
          ) => {
            if (group.id === employee.company.id) {
              acc = {
                ...acc,
                ...group,
                attorneys: acc.attorneys
                  ? [...acc.attorneys, ...group.attorneys]
                  : [...group.attorneys],
                roles: acc.roles
                  ? [...acc.roles, ...group.roles]
                  : [...group.roles],
              };
            }
            return acc;
          },
          { id: '', name: '', attorneys: [], roles: [] },
        ),
      })),
      has_company_side: user.has_company_side,
      permissions: {
        editSubstitutes: user.employees.some(
          (employee) => employee.permissions.edit_substitutes,
        ),
      },
      ukepCertificates: this.processUkepCertificates(user?.ukep_certificates),
      attorneys: user.attorneys.map((attorney) =>
        this.processAttorney(attorney),
      ),
    };
  }

  processUkepCertificates(certificates: UkepCertificate[] | undefined) {
    if (!certificates) return;

    const uniqueCertificates = new Map();

    certificates.forEach((certificate) => {
      if (uniqueCertificates.has(certificate.serial_number)) {
        const current = uniqueCertificates.get(certificate.serial_number);
        uniqueCertificates.set(certificate.serial_number, {
          ...current,
          company_name: `${current.company_name}, ${certificate.company_name}`,
        });
      } else {
        uniqueCertificates.set(certificate.serial_number, certificate);
      }
    });

    return Array.from(uniqueCertificates.values()).map((certificate) => ({
      activity:
        certificate.valid_from && certificate.valid_to
          ? `Действителен: с ${this.format.toDate(
              certificate.valid_from,
            )} по ${this.format.toDate(certificate.valid_to)}`
          : '',
      companyId: certificate.company_id,
      companyName: `Загружен для: ${certificate.company_name}`,
      description: 'УКЭП (Усиленная квалифицированная электронная подпись)',
      issuedBy: `Выдан: ${certificate.issued_by}`,
      owner: `Владелец: ${certificate.owner}`,
      serialNumber: `Сертификат: ${certificate.serial_number}`,
      type: 'УКЭП',
    }));
  }

  processAttorneyStatus(
    status: AttorneyStatus,
    isDefault: boolean,
  ): AttorneyReadableStatus {
    if (status === 'new') {
      return 'На подписании';
    }

    if (status === 'expired') {
      return 'Истекла';
    }

    if (status === 'revoked') {
      return 'Отозвана';
    }

    if (status === 'canceled') {
      return 'Отключена';
    }

    if (isDefault && status === 'signed') {
      return 'Активна';
    }

    if (isDefault && status === 'revoking') {
      return 'В процессе отзыва';
    }

    return 'Действующая';
  }

  processAttorneyStatusToTypographyColor(
    attorneyStatus: AttorneyStatus,
    attorneyIsDefault: boolean,
  ): TypographyColor {
    if (attorneyStatus === 'new') {
      return 'accent.text.warning';
    }

    if (attorneyIsDefault && attorneyStatus === 'signed') {
      return 'accent.text.success';
    }

    if (attorneyIsDefault && attorneyStatus === 'revoking') {
      return 'accent.text.warning';
    }

    return 'text.light.tertirary';
  }

  processAttorneyStatusToChipColor(
    attorneyStatus: AttorneyStatus,
  ): ChipColorUnion {
    if (attorneyStatus === 'new') {
      return 'blue';
    }

    if (attorneyStatus === 'signed') {
      return 'green';
    }

    if (attorneyStatus === 'revoking') {
      return 'orange';
    }

    if (attorneyStatus === 'revoked') {
      return 'red';
    }

    if (attorneyStatus == 'canceled') {
      return 'purple';
    }

    return 'gray';
  }

  processAttorney(attorney: Attorney): AttorneyType {
    return {
      id: attorney.id,
      UID: attorney.id,
      status: attorney.status,
      readableStatus: this.processAttorneyStatus(
        attorney.status,
        attorney.is_default,
      ),
      snils: this.format.toSnils(attorney.snils),
      issuer: attorney.issuer,
      isDefault: attorney.is_default,
      creationDate: this.format.toDate(attorney.creation_date),
      validFrom: this.format.toDate(attorney.valid_from),
      validTo: this.format.toDate(attorney.valid_to),
      companyId: attorney.company.id,
      companyName: attorney.company.name,
      powers: attorney.powers || ATTORNEYS_POWERS_DEFAULT,
      revocationReason: attorney.revocation_reason || '',
      imported: attorney.imported,
      fullName: attorney.full_name,
      typographyColor: this.processAttorneyStatusToTypographyColor(
        attorney.status,
        attorney.is_default,
      ),
      chipColor: this.processAttorneyStatusToChipColor(attorney.status),
    };
  }

  processGroups(groups: UserGroup[], attorneys: Attorney[]) {
    const companies: Record<
      string,
      {
        id: string;
        name: string;
        roles: { id: string; name: string; profiles?: ProfileItem[] }[];
        attorneys: AttorneyType[];
      }
    > = {};

    groups.forEach((group) => {
      if (group.company.id in companies) {
        companies[group.company.id].roles.push({
          id: group.id,
          name: group.name,
          profiles: group.profiles,
        });
      } else {
        companies[group.company.id] = {
          id: group.company.id,
          name: group.company.name,
          roles: [{ id: group.id, name: group.name, profiles: group.profiles }],
          attorneys: attorneys
            .filter(
              (attorney) =>
                attorney.company.id === group.company.id &&
                (attorney.status === 'new' ||
                  attorney.status === 'signed' ||
                  attorney.status === 'revoking'),
            )
            .map((attorney) => this.processAttorney(attorney)),
        };
      }
    });

    return Object.keys(companies).map((id) => companies[id]);
  }

  processEmployees(employee: ManagerSubstitute) {
    const { substitute, dates } = employee;

    return {
      id: substitute.id,
      name: substitute.name,
      personnelNumber: substitute.personnel_number,
      email: substitute.email,
      position: substitute.position,
      phone: substitute.phone,
      company: {
        id: substitute.company.id,
        name: substitute.company.name,
      },
      dates: dates || [],
    };
  }

  processManagerSubstitutesEventType(
    event_types: ManagerSubstitutesEventType[],
  ) {
    return event_types.map(({ substitutes, event_type }) => ({
      substitutes: substitutes.map((substitute) => ({
        ...this.processEmployees(substitute),
      })),
      eventType: {
        id: event_type.id,
        name: event_type.name,
      },
    }));
  }

  processSubstitutes(
    items: ManageableCompanyItem[],
  ): CompanyPositionSubstitutes[] {
    return items.map(
      ({ id, name, employee, substitutes, functional_manageable_units }) => ({
        company: { id, name },
        employee: {
          id: employee.id,
          personalNumber: employee.personnel_number,
          position: employee.position || '',
          showPosition: items.filter((item) => item.id === id).length > 1,
        },
        eventTypeGroup: substitutes.map(
          ({ event_type, substitutes: eventTypeSubstitutes }) => ({
            eventType: {
              id: event_type.id,
              name: event_type.name,
            },
            substitutes: eventTypeSubstitutes
              .map(({ substitute, dates }) => ({
                id: substitute.id,
                name: substitute.name,
                personalNumber: substitute.personnel_number,
                position: substitute.position,
                company: substitute.company.name,
                unit_name: substitute.unit_name,
                dates: dates || [],
              }))
              .sort((a, b) => a.name.localeCompare(b.name)),
          }),
        ),
        manageableUnits: functional_manageable_units?.map((item) => ({
          unit: {
            id: item.unit.id,
            name: item.unit.name,
          },
          roleInUnit: {
            label: this.processRoleInUnit(item.role_in_unit),
            role: item.role_in_unit,
          },
          allSetting: item.event_type_settings
            ?.filter((settings) => settings.approve === 'always')
            ?.map((settings) => settings.event_type),
          directSetting: item.event_type_settings
            ?.filter((settings) => settings.approve === 'direct_only')
            ?.map((settings) => settings.event_type),
          neverSetting: item.event_type_settings
            ?.filter((settings) => settings.approve === 'never')
            ?.map((settings) => settings.event_type),
        })),
      }),
    );
  }

  processCompanyEmployeeEventTypesSubstitutes({
    employee,
    company,
    eventTypeGroup,
  }: CompanyPositionSubstitutes) {
    const groupedEventTypesBySubstitute: Record<
      string,
      Record<string, SubstituteType>
    > = {};

    eventTypeGroup.forEach(({ substitutes, eventType }) => {
      substitutes.forEach(({ id, dates }) => {
        groupedEventTypesBySubstitute[id] = {
          ...groupedEventTypesBySubstitute[id],
          [eventType.id]: !!dates?.length ? 'temporary' : 'permanent',
        };
      });
    });

    return { company, employee, groupedEventTypesBySubstitute };
  }

  processRoleInUnit(roleInUnit: 'manager' | 'assistant') {
    switch (roleInUnit) {
      case 'manager':
        return 'Руководитель';
      case 'assistant':
        return 'Ассистент';
      default:
        return '';
    }
  }

  processToken1CExpiresAtDate(expiresAt: string) {
    return this.format.toDate(expiresAt);
  }

  processSubstitutesOptions(options: ManagerSubstituteAbsenceOption[]) {
    const loadedOptions = options.map(({ from_date, to_date, type }) => ({
      label: `${this.format.toDateRange(from_date, to_date)}, ${type}`,
      value: `${from_date} - ${to_date}`,
    }));

    return loadedOptions;
  }

  processSubstituteDates(dates?: DateRange[]) {
    if (!dates?.length) {
      return 'Постоянный';
    }

    const substituteDatesLabel =
      this.format.toDateRangeWithDaysBetweenInclusive(
        dates[0].from_date,
        dates[0].to_date,
      );

    if (dates.length > 1) {
      return `${substituteDatesLabel} и еще ${dates.length - 1}`;
    }

    return substituteDatesLabel;
  }
}
